import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Atmosphere } from "../components/Atmosphere";
import { books } from "../data/books";
import { ExternalLink, Lock, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";

export default function Shop() {
  // 1. Sprache aus dem Speicher holen & Event-Lauscher für die Navbar aufsetzen
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("project_lang") || "de";
  });

  useEffect(() => {
    const handleLangChange = () => {
      setCurrentLang(localStorage.getItem("project_lang") || "de");
    };
    window.addEventListener("languageChange", handleLangChange);
    return () => window.removeEventListener("languageChange", handleLangChange);
  }, []);

  // Sammlerstücke lokal übersetzt definieren
  const collectibles = [
    { 
      name: currentLang === "en" ? "Imperial Bookmark (Placeholder)" : "Kaiserliches Lesezeichen (Platzhaltertitel)", 
      desc: currentLang === "en" ? "Currently in development." : "Dieser Bereich befindet sich noch in der Entwicklung.", 
      color: "#C9A227" 
    },
    { 
      name: currentLang === "en" ? "Dragon Artprint A3 (Placeholder)" : "Drachen-Artprint A3 (Platzhaltertitel)", 
      desc: currentLang === "en" ? "Currently in development." : "Dieser Bereich befindet sich noch in der Entwicklung.", 
      color: "#B01218" 
    },
    { 
      name: currentLang === "en" ? "Jade Pendant (Placeholder)" : "Jadeanhänger (Platzhaltertitel)", 
      desc: currentLang === "en" ? "Currently in development." : "Dieser Bereich befindet sich noch in der Entwicklung.", 
      color: "#0F8B6D" 
    },
    { 
      name: currentLang === "en" ? "Map of Ruìhuá (Placeholder)" : "Karte von Ruìhuá (Platzhaltertitel)", 
      desc: currentLang === "en" ? "Currently in development." : "Dieser Bereich befindet sich noch in der Entwicklung.", 
      color: "#E8DDC8" 
    },
  ];

  return (
    <div className="pt-32 pb-24 px-6 relative" data-testid="shop-page">
      <Atmosphere variant="minimal" />
      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <p className="chinese text-[#C9A227]/70 text-2xl mb-3">書坊</p>
          <h1 className="serif text-5xl lg:text-7xl text-[#E8DDC8] mb-4">
            {currentLang === "en" ? (
              <>The <span className="italic text-[#C9A227]">Shop</span></>
            ) : (
              <>Der <span className="italic text-[#C9A227]">Shop</span></>
            )}
          </h1>
          <p className="serif italic text-[#B3B6B7] text-lg max-w-2xl mx-auto">
            {currentLang === "en" 
              ? "Books and collectibles from the Empire. Coming soon. (This environment is still under development. Some features may not be available.)"
              : "Bücher und Sammlerstücke aus dem Kaiserreich. Bald erhältlich.(Diese Umgebung befindet sich noch in der Entwicklung. Möglicherweise sind einige Funktionen nicht erreichbar.)"}
          </p>
        </motion.div>

        {/* Books */}
        <h2 className="serif text-3xl text-[#E8DDC8] mb-8">
          {currentLang === "en" ? "The Six Volumes" : "Die Sechs Bände"}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {books.map((b) => {
            // Falls b.title ein Objekt ist, passend auflösen, sonst Fallback auf String
            const bookTitle = typeof b.title === "object" ? (b.title[currentLang] || b.title["de"]) : b.title;
            
            return (
              <div key={b.id} className="card-imperial overflow-hidden group" data-testid={`shop-book-${b.id}`}>
                <div className="aspect-[2/3] relative flex items-center justify-center overflow-hidden">
                  {b.coverUrl && (
                    <img 
                      src={b.coverUrl} 
                      alt={bookTitle} 
                      className="absolute inset-0 w-full h-full object-cover z-0 transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                  <div 
                    className="absolute inset-0 z-10 transition-opacity duration-500 group-hover:opacity-0"
                    style={{ background: `linear-gradient(160deg, ${b.color}55 0%, #080808 90%)` }}
                  />
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center p-6 transition-all duration-500 group-hover:opacity-0 group-hover:scale-95">
                    <span className="chinese text-9xl opacity-15 absolute select-none" style={{ color: b.color }}>書</span>
                    <div className="absolute inset-4 border border-[#C9A227]/25" />
                    <div className="text-center relative z-10">
                      <p className="serif text-6xl mb-2" style={{ color: b.color }}>{b.number}</p>
                      <h3 className="serif text-xl text-[#E8DDC8]">{bookTitle}</h3>
                    </div>
                  </div>
                </div>
                <div className="p-5 border-t border-[#C9A227]/15 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#7A7C7D] flex items-center gap-2">
                    <Lock size={12} /> {currentLang === "en" ? "Coming soon" : "Bald verfügbar"}
                  </span>
                  <a
                    href="https://www.amazon.com/kindle-dbs/storefront"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-xs uppercase tracking-[0.25em] text-[#C9A227] hover:text-[#E8DDC8] flex items-center gap-1"
                    data-testid={`kdp-link-${b.id}`}
                  >
                    KDP <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        

        {/* Collectibles */}
        <h2 className="serif text-3xl text-[#E8DDC8] mb-8">
          {currentLang === "en" ? "Collectibles" : "Sammlerstücke"}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {collectibles.map((c, i) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="card-imperial p-6"
              data-testid={`collectible-${i}`}
            >
              <div
                className="w-full aspect-square mb-4 flex items-center justify-center border"
                style={{ borderColor: `${c.color}55`, background: `${c.color}11` }}
              >
                <span className="chinese text-7xl opacity-25" style={{ color: c.color }}>璽</span>
              </div>
              <h3 className="serif text-xl text-[#E8DDC8]">{c.name}</h3>
              <p className="text-sm text-[#B3B6B7] mt-1">{c.desc}</p>
              <p className="mt-4 text-xs uppercase tracking-[0.25em] text-[#7A7C7D] flex items-center gap-2">
                <Lock size={12} /> {currentLang === "en" ? "Coming soon" : "Bald verfügbar"}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Newsletter / Follow CTA */}
        <div className="mt-20 card-imperial p-10 text-center">
          <h3 className="serif text-3xl text-[#E8DDC8] mb-3">
            {currentLang === "en" ? "Be the First to Know" : "Erfahre als Erste*r"}
          </h3>
          <p className="text-[#B3B6B7] mb-6">
            {currentLang === "en"
              ? "The doors of the library will open soon. Follow the project for updates on Amazon KDP."
              : "Die Tore der Bibliothek öffnen sich bald. Folge dem Projekt für Updates auf Amazon KDP."}
          </p>
          <a
            href="https://www.amazon.com/kindle-dbs/storefront"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="main-kdp-link"
          >
            <button className="btn-imperial">
              {currentLang === "en" ? "Follow on Amazon KDP" : "Auf Amazon KDP folgen"} <ExternalLink size={14} />
            </button>
          </a>
        </div>
      </div>
    </div>
  );
}